import axois, { CancelTokenSource } from "axios";
import { ItemsWithCounting } from "src/models";
import {
  ObjectItem,
  ObjectType,
  Storage,
  Source,
  Tag,
  ClassItem,
  Markup,
  MarkupType,
  ClassGroupItem,
} from "src/modules/dataset/models";
import {
  ClassGroupSaveRequest,
  ClassSaveRequest,
  ObjectsGetListRequest,
  ObjectsMarkClassesRequest,
  ObjectsMarkTagsRequest,
  TagSaveRequest,
} from "./dto";
import { BaseApiService } from "../api-base";

export class SqlrDatasetApiService extends BaseApiService {
  constructor(host: string) {
    super("sqlr_dataset");
    this.http = axois.create({
      baseURL: host,
    });
    this.http.interceptors.request.use((config) => {
      config.headers.Authorization = this.jwt;
      return config;
    });
  }

  private getFullPath(method: string) {
    return `${this.url}/rpc/${method}`;
  }

  //#region objects
  data_object_save(data: ObjectItem) {
    const method = this.getFullPath("data_object_save");
    const body = {
      ...data,
      created_at: undefined,
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }

  data_objects_get_list(
    params: ObjectsGetListRequest,
    cancelTokenSource: CancelTokenSource | undefined = undefined,
  ) {
    const method = this.getFullPath("data_objects_get_list");
    return this.http
      .post<ObjectItem[]>(method, params, {
        cancelToken: cancelTokenSource?.token,
      })
      .then((response) => response.data);
  }

  data_objects_get_list_sub_count(
    params: ObjectsGetListRequest,
    cancelTokenSource: CancelTokenSource | undefined = undefined,
  ) {
    const method = this.getFullPath("data_objects_get_list_sub_count");
    return this.http
      .post<ObjectItem[]>(method, params, {
        cancelToken: cancelTokenSource?.token,
      })
      .then((response) => response.data);
  }

  data_objects_delete(
    obj_guid: string[],
    cancelTokenSource: CancelTokenSource | undefined = undefined,
  ) {
    const method = this.getFullPath("data_object_delete");
    return this.http
      .post<ObjectItem[]>(
        method,
        { obj_guid },
        {
          cancelToken: cancelTokenSource?.token,
        },
      )
      .then((response) => response.data);
  }

  data_objects_get_list_with_count(params: ObjectsGetListRequest) {
    const method = this.getFullPath("data_objects_get_list_with_count");
    return this.http
      .post<ItemsWithCounting<ObjectItem>>(method, params)
      .then((response) => response.data);
  }
  data_objects_get_count(params: ObjectsGetListRequest) {
    const method = this.getFullPath("data_objects_get_count");
    const body = {
      ...params,
      lim: undefined,
      offs: undefined,
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  data_object_get(obj_guid: string) {
    const method = this.getFullPath("data_object_get");
    const config = { params: { obj_guid } };
    return this.http
      .get<ObjectItem>(method, config)
      .then((response) => response.data);
  }
  data_objects_set_classes(params: ObjectsMarkClassesRequest) {
    const method = this.getFullPath("data_objects_set_classes");
    return this.http.post(method, params).then((response) => response.data);
  }
  data_objects_remove_classes(params: ObjectsMarkClassesRequest) {
    const method = this.getFullPath("data_objects_remove_classes");
    return this.http.post(method, params).then((response) => response.data);
  }
  data_objects_set_tags(params: ObjectsMarkTagsRequest) {
    const method = this.getFullPath("data_objects_set_tags");
    return this.http.post(method, params).then((response) => response.data);
  }
  data_objects_remove_tags(params: ObjectsMarkTagsRequest) {
    const method = this.getFullPath("data_objects_remove_tags");
    return this.http.post(method, params).then((response) => response.data);
  }
  //#endregion

  //#region object_types
  data_object_type_save(data: ObjectType) {
    const method = this.getFullPath("data_object_type_save");
    return this.http
      .post<string>(method, data)
      .then((response) => response.data);
  }

  data_object_types_get_list() {
    const method = this.getFullPath("data_object_types_get_list");
    return this.http
      .get<ObjectType[]>(method)
      .then((response) => response.data);
  }
  //#endregion

  //#region storages
  data_storages_get_list() {
    const method = this.getFullPath("data_storages_get_list");
    return this.http.get<Storage[]>(method).then((response) => response.data);
  }
  //#endregion

  //#region sources
  data_sources_get_list() {
    const method = this.getFullPath("data_sources_get_list");
    return this.http.get<Source[]>(method).then((response) => response.data);
  }
  //#endregion

  //#region tags
  data_tag_save(data: Tag) {
    const method = this.getFullPath("data_tag_save");
    const body: TagSaveRequest = {
      tag_guid: data.tag_guid,
      type: data.type,
      parent_guid: data.parent_guid || undefined,
      name: data.name,
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }

  data_tag_delete(tag_guid: string) {
    const method = this.getFullPath("data_tag_delete");
    const body = { tag_guid };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }

  data_tag_delete_with_objects(tag_guid: string) {
    const method = this.getFullPath("data_tag_delete_with_objects");
    const body = { tag_guid };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }

  data_tags_get_list() {
    const method = this.getFullPath("data_tags_get_list");
    return this.http.get<Tag[]>(method).then((response) => response.data);
  }
  //#endregion

  //#region classes
  data_classes_get_list(body?: {
    limit?: number;
    offset?: number;
    name?: string;
  }) {
    const method = this.getFullPath("data_classes_get_list");
    return this.http
      .post<ClassItem[]>(method, {
        offset: body?.offset,
        limit: body?.limit,
        name: body?.name,
      })
      .then((response) => response.data);
  }
  data_class_save(data: ClassItem) {
    const method = this.getFullPath("data_class_save");
    const body: ClassSaveRequest = {
      class_guid: data.class_guid,
      name: data.name,
      key: data.key,
      comment: data.comment,
      example_question: data.example_question,
      example_answer: data.example_answer,
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  data_class_delete(class_guid: string) {
    const method = this.getFullPath("data_class_delete");
    const body = { class_guid };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  //#endregion

  //#region class_groups
  data_class_groups_get(class_group_guid: string) {
    const method = this.getFullPath("data_class_groups_get_list");
    const params = { class_group_guid: `eq.${class_group_guid}` };
    return this.http
      .post<ClassGroupItem>(
        method,
        { is_class: true },
        {
          params: params,
          headers: {
            Accept: "application/vnd.pgrst.object+json",
          },
        },
      )
      .then((response) => response.data);
  }
  data_class_groups_get_list() {
    const method = this.getFullPath("data_class_groups_get_list");
    return this.http
      .get<ClassGroupItem[]>(method)
      .then((response) => response.data);
  }

  data_class_group_get(body: {
    guid: string;
    name?: string;
    limit?: number | null;
    offset?: number;
  }) {
    const method = this.getFullPath("data_class_group_get");
    return this.http
      .post(method, { ...body, name: body.name ? `%${body.name}%` : undefined })
      .then((response) => response.data);
  }
  data_class_group_save(data: ClassGroupItem) {
    const method = this.getFullPath("data_class_group_save");
    const body: ClassGroupSaveRequest = {
      class_group_guid: data.class_group_guid,
      name: data.name,
      classes: data.classes.map((cl) => cl.guid),
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  data_class_group_delete(class_group_guid: string) {
    const method = this.getFullPath("data_class_group_delete");
    const body = { class_group_guid };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  //#endregion

  //#region markups
  data_markup_types_get_list() {
    const method = this.getFullPath("data_markup_types_get_list");
    const config = { params: {} };
    return this.http
      .get<MarkupType[]>(method, config)
      .then((response) => response.data);
  }
  data_markup_get(markup_guid: string) {
    const method = this.getFullPath("data_markup_get");
    const config = { params: { markup_guid } };
    return this.http
      .get<Markup>(method, config)
      .then((response) => response.data);
  }
  data_markups_get_list_by_object(obj_guid: string) {
    const method = this.getFullPath("data_markups_get_list_by_object");
    const config = { params: { obj_guid } };
    return this.http
      .get<Markup[]>(method, config)
      .then((response) => response.data);
  }
  data_markup_save(obj_guid: string, markup: Markup): Promise<string> {
    const method = this.getFullPath("data_markup_save");
    const body = {
      markup_guid: markup.markup_guid,
      type_guid: markup.type_guid,
      user_ref: markup.user_ref,
      content: markup.content,
      obj_guid,
    };
    return this.http
      .post<string>(method, body)
      .then((response) => response.data);
  }
  //#endregion
}
